/* Costack CRO - v7 20 May 2026
 * v7 fix: the add-on chooser tiles used to POST /cart/add.js with NO sections
 * parameter and never re-rendered the drawer. The cart grew server-side but the
 * subtotal, free-ship bar and line items stayed frozen on screen. Now the tile
 * add uses the sections parameter and calls cart-drawer.renderContents() - the
 * exact path Shopify uses - so items + subtotal + free-ship bar all refresh
 * together the moment a tile is tapped.
 *
 * What this file does:
 *  - PDP shipping note ("FREE UK delivery on orders over X")
 *  - Cart drawer add-on chooser (4 tiles below threshold)
 *  - Refresh add-on chooser on every cart mutation
 *
 * Single source of truth for the progress bar lives in snippets/cart-drawer.liquid
 * (Liquid-rendered). This file never draws the bar.
 */
(function () {
  'use strict';

  var CFG = window.CostackCRO || {};
  var THRESHOLD = (CFG.freeShippingThresholdPence != null) ? CFG.freeShippingThresholdPence : 3500;
  var PAID_SHIPPING_LABEL = CFG.paidShippingLabel || 'Standard UK delivery from £2.70';
  var CURRENCY = CFG.currencySymbol || '£';

  var ADDONS = CFG.addOns || [
    { id: 46059639570722, name: 'Mild Curry',  price: 299 },
    { id: 55517361668470, name: 'Vegetable',   price: 299 },
    { id: 55517359473014, name: 'All Purpose', price: 299 },
    { id: 46263515480354, name: 'Complete',    price: 299 }
  ];

  function fmt(pence) { return CURRENCY + (pence / 100).toFixed(2); }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  var isAddingAddon = false;

  // PDP shipping note
  function ensurePDPMount() {
    var form = document.querySelector('product-form, .product-form');
    if (!form) return null;
    var existing = form.parentNode.querySelector('.costack-pdp-shipping');
    if (existing) return existing;
    var note = document.createElement('div');
    note.className = 'costack-pdp-shipping';
    note.innerHTML =
      '<span class="costack-pdp-shipping__icon" aria-hidden="true">🚚</span>'
      + '<span class="costack-pdp-shipping__text">'
      +   '<strong>FREE UK delivery</strong> on orders over ' + fmt(THRESHOLD) + '. '
      +   PAID_SHIPPING_LABEL + '. Delivery in 2 to 5 working days.'
      + '</span>';
    form.parentNode.insertBefore(note, form);
    return note;
  }

  // Add-on chooser mount inside cart drawer footer area
  function ensureAddonMount() {
    var drawer = document.querySelector('cart-drawer .drawer__inner') || document.querySelector('cart-drawer');
    if (!drawer) return null;
    var mount = drawer.querySelector('.costack-bar-mount');
    if (mount) return mount;
    mount = document.createElement('div');
    mount.className = 'costack-bar-mount';
    // Sit inside the drawer footer area, above the trust block
    var footer = drawer.querySelector('.cart-drawer__footer');
    var trust = drawer.querySelector('.cart-trust');
    if (footer && trust) {
      footer.insertBefore(mount, trust);
    } else if (footer) {
      footer.insertBefore(mount, footer.firstChild);
    } else {
      drawer.appendChild(mount);
    }
    return mount;
  }

  function renderAddons(cart, mount) {
    if (!mount || !cart) return;
    // Hide entirely when cart empty OR already unlocked
    if (!cart.item_count || (cart.total_price >= THRESHOLD)) {
      mount.innerHTML = '';
      return;
    }
    var html = '<div class="costack-bar costack-bar--addons-only">'
      + '<div class="costack-bar__addons-header">Top up with a ' + fmt(299) + ' favourite:</div>'
      + '<div class="costack-bar__addons">';
    for (var i = 0; i < ADDONS.length; i++) {
      var a = ADDONS[i];
      html += '<button type="button" class="costack-bar__addon" data-variant-id="' + a.id + '" data-original-name="' + escapeHtml(a.name) + '">'
        +   '<span class="costack-bar__addon-name">' + escapeHtml(a.name) + '</span>'
        +   '<span class="costack-bar__addon-price">+ ' + fmt(a.price) + '</span>'
        + '</button>';
    }
    html += '</div></div>';
    mount.innerHTML = html;
  }

  function refreshAddons() {
    if (isAddingAddon) return Promise.resolve();
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderAddons(cart, ensureAddonMount());
      })
      .catch(function () {});
  }

  // Manual section render fallback if the cart-drawer custom element has not upgraded.
  function fallbackRender(response) {
    if (!response || !response.sections) return;
    Object.keys(response.sections).forEach(function (sectionId) {
      var html = response.sections[sectionId];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      if (sectionId === 'cart-drawer') {
        var src = doc.querySelector('#CartDrawer');
        var dst = document.querySelector('#CartDrawer');
        if (src && dst) dst.innerHTML = src.innerHTML;
      } else if (sectionId === 'cart-icon-bubble') {
        var srcB = doc.querySelector('#cart-icon-bubble') || doc.body.firstElementChild;
        var dstB = document.querySelector('#cart-icon-bubble');
        if (srcB && dstB) dstB.innerHTML = srcB.innerHTML;
      }
    });
  }

  // Capture-phase click handler for add-on tiles.
  // v7: adds with the sections parameter and re-renders the whole drawer so the
  // subtotal and free-ship bar move the instant a tile is tapped.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.costack-bar__addon');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (isAddingAddon) return;
    if (btn.classList.contains('is-adding')) return;

    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;

    isAddingAddon = true;
    btn.classList.add('is-adding');
    var nameEl = btn.querySelector('.costack-bar__addon-name');
    var priceEl = btn.querySelector('.costack-bar__addon-price');
    var originalName = btn.getAttribute('data-original-name') || '';
    if (nameEl) nameEl.textContent = 'Adding...';
    if (priceEl) priceEl.textContent = '';

    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    var formData = new FormData();
    formData.append('id', String(parseInt(vid, 10)));
    formData.append('quantity', '1');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      formData.append('sections_url', window.location.pathname);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || response.message || 'Add to cart failed');
        }
        if (nameEl) nameEl.textContent = 'Added';
        // Full drawer re-render via Shopify's own path: line items + subtotal + free-ship bar.
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          fallbackRender(response);
        }
        setTimeout(function () {
          isAddingAddon = false;
          refreshAddons();
        }, 250);
      })
      .catch(function () {
        if (nameEl && originalName) nameEl.textContent = originalName;
        if (priceEl) priceEl.textContent = '+ ' + fmt(299);
        if (btn && btn.classList) btn.classList.remove('is-adding');
        isAddingAddon = false;
      });
  }, true);

  function init() {
    ensurePDPMount();
    refreshAddons();

    // Refresh the addon chooser whenever the cart changes via fetch
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = (args[0] && args[0].url) || String(args[0] || '');
      var p = origFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () { setTimeout(refreshAddons, 200); }).catch(function () {});
      }
      return p;
    };

    document.documentElement.addEventListener('cart:refresh', function () {
      setTimeout(refreshAddons, 50);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Costack cart-drawer UX cleanup - 23 Jun 2026 (Argos-style simplification)
 * DOM-level only (the drawer markup lives in a 39KB snippet). Runs site-wide:
 *  - Shortens the checkout button to "Checkout"
 *  - Hides the confusing "open in browser / Apple Pay" instruction line
 *  - Hides the duplicated "Order special instructions" label
 *  - Adds a "Continue shopping" link in the footer
 *  - Shows a brief "Added to basket" affirmation when an item is added
 * Re-applies on every drawer re-render via MutationObserver.
 */
(function () {
  'use strict';

  function clean(drawer) {
    if (!drawer) return;

    var co = drawer.querySelector('#CartDrawer-Checkout, [name="checkout"]');
    if (co && /proceed|secure checkout/i.test(co.textContent || '')) {
      co.textContent = 'Checkout';
    }

    var leaves = drawer.querySelectorAll('p, span, div, small, li');
    for (var i = 0; i < leaves.length; i++) {
      var el = leaves[i];
      if (el.children.length) continue;
      var t = (el.textContent || '').trim();
      if (/open in browser/i.test(t) || /for apple pay\s*&?\s*faster checkout/i.test(t)) {
        var wrap = el.closest('p, div, li, small') || el;
        wrap.style.display = 'none';
      }
    }

    var notes = [];
    var labels = drawer.querySelectorAll('label');
    for (var j = 0; j < labels.length; j++) {
      if (/order special instructions/i.test(labels[j].textContent || '')) notes.push(labels[j]);
    }
    for (var k = 1; k < notes.length; k++) {
      var n = notes[k];
      var box = n.closest('.cart__note, .cart-drawer__note, div') || n;
      box.style.display = 'none';
    }

    if (!drawer.querySelector('.cstk-continue')) {
      var footer = drawer.querySelector('.cart-drawer__footer, .drawer__footer');
      if (footer) {
        var a = document.createElement('a');
        a.href = '/collections/all';
        a.className = 'cstk-continue';
        a.textContent = 'Continue shopping';
        a.setAttribute('style', 'display:block;text-align:center;margin-top:10px;font-size:13px;color:#231900;text-decoration:underline;');
        footer.appendChild(a);
      }
    }
  }

  function affirm() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.querySelector('.cstk-added')) return;
    var host = drawer.querySelector('.drawer__header, .cart-drawer__header');
    var d = document.createElement('div');
    d.className = 'cstk-added';
    d.setAttribute('role', 'status');
    d.textContent = '✓ Added to basket';
    d.setAttribute('style', 'margin:10px 16px;padding:8px 12px;background:#e9f8ec;color:#1f7a37;border-radius:8px;font-size:13px;font-weight:600;');
    if (host && host.parentNode) host.parentNode.insertBefore(d, host.nextSibling);
    else drawer.insertBefore(d, drawer.firstChild);
    setTimeout(function () { if (d && d.parentNode) d.parentNode.removeChild(d); }, 4000);
  }

  function boot() {
    var drawer = document.querySelector('cart-drawer');
    clean(drawer);
    if (drawer && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () { clean(document.querySelector('cart-drawer')); });
      mo.observe(drawer, { childList: true, subtree: true });
    }
    var of = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = (args[0] && args[0].url) || String(args[0] || '');
      var p = of.apply(this, args);
      if (/\/cart\/add/.test(url)) { p.then(function () { setTimeout(affirm, 350); }).catch(function () {}); }
      return p;
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
